import React, { useState, useEffect } from 'react';
import { Card, Form, Input, Switch, Button, message, Space, Typography, Spin, Row, Col, Badge, Empty } from 'antd';
import { SafetyCertificateOutlined, SaveOutlined, RightOutlined, SettingOutlined } from '@ant-design/icons';
import { baseurl } from '../../../components/lib/settings';

const { Title, Text } = Typography;
const { TextArea } = Input;

const AdminDisponibilidade = () => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [sessionData, setSessionData] = useState(null);
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        fetchAvailability();
    }, []);

    const fetchAvailability = async () => {
        setLoading(true);
        try {
            const response = await baseurl.get('v2/ngoma/check_availability/?msisdn=admin');
            if (response.data && response.data.length > 0) {
                const data = response.data[0];
                setSessionData(data);
                form.setFieldsValue({
                    description: data.description,
                    ngoma_availability: data.ngoma_availability
                });
            } else {
                // Se não existir, criar um mock para poder ser configurado pela primeira vez
                const mockData = { id: null, description: "Plataforma em manutenção.", ngoma_availability: false };
                setSessionData(mockData);
                form.setFieldsValue(mockData);
            }
        } catch (error) {
            message.error("Erro ao carregar o estado do sistema.");
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async (values) => {
        setSubmitting(true);
        try {
            const payload = { ...values };
            if (sessionData && sessionData.id) {
                payload.id = sessionData.id;
            }
            
            await baseurl.put('v2/ngoma/check_availability/', payload);
            message.success("Configurações guardadas com sucesso!");
            fetchAvailability();
            setIsEditing(false);
        } catch (error) {
            message.error(error.response?.data?.error || "Erro ao atualizar.");
        } finally {
            setSubmitting(false);
        }
    };

    const toggleEdit = () => {
        setIsEditing(true);
    };

    return (
        <div style={{ padding: '24px', minHeight: '80vh' }}>
            <div style={{ marginBottom: '24px' }}>
                <Space>
                    <SafetyCertificateOutlined style={{ fontSize: '28px', color: '#1890ff' }} />
                    <Title level={3} style={{ margin: 0, fontFamily: 'Montserrat, sans-serif' }}>
                        Controlo de Acesso
                    </Title>
                </Space>
                <p style={{ color: '#666', marginTop: '8px' }}>
                    Gira a disponibilidade global da plataforma e as mensagens de manutenção.
                </p>
            </div>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '100px 0' }}>
                    <Spin size="large" />
                </div>
            ) : (
                <Row gutter={[24, 24]}>
                    {/* COLUNA ESQUERDA - LISTA DE OCORRÊNCIAS */}
                    <Col xs={24} lg={isEditing ? 10 : 24} style={{ transition: 'all 0.3s ease' }}>
                        <Card 
                            hoverable 
                            onClick={toggleEdit}
                            style={{ 
                                borderRadius: '16px', 
                                border: isEditing ? '2px solid #1890ff' : '1px solid #f0f0f0',
                                boxShadow: isEditing ? '0 8px 24px rgba(24, 144, 255, 0.15)' : '0 4px 12px rgba(0,0,0,0.05)',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease'
                            }}
                            bodyStyle={{ padding: '24px' }}
                        >
                            <Row align="middle" justify="space-between">
                                <Col>
                                    <Space direction="vertical" size={2}>
                                        <Text type="secondary" style={{ fontSize: '12px', textTransform: 'uppercase', letterSpacing: '1px' }}>
                                            Configuração Principal
                                        </Text>
                                        <Title level={4} style={{ margin: 0 }}>
                                            Estado Global do Sistema
                                        </Title>
                                        <Space style={{ marginTop: '12px' }}>
                                            <Badge status={sessionData?.ngoma_availability ? 'success' : 'error'} />
                                            <Text strong style={{ color: sessionData?.ngoma_availability ? '#52c41a' : '#f5222d' }}>
                                                {sessionData?.ngoma_availability ? 'SISTEMA ONLINE' : 'EM MANUTENÇÃO'}
                                            </Text>
                                        </Space>
                                    </Space>
                                </Col>
                                <Col>
                                    <div style={{ 
                                        width: '40px', 
                                        height: '40px', 
                                        borderRadius: '50%', 
                                        background: isEditing ? '#1890ff' : '#f5f5f5',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition: 'all 0.3s ease'
                                    }}>
                                        <RightOutlined style={{ color: isEditing ? '#fff' : '#888' }} />
                                    </div>
                                </Col>
                            </Row>
                        </Card>
                    </Col>

                    {/* COLUNA DIREITA - FORMULÁRIO DE EDIÇÃO */}
                    {isEditing && (
                        <Col xs={24} lg={14} style={{ animation: 'fadeInRight 0.4s ease' }}>
                            <Card
                                title={
                                    <Space>
                                        <SettingOutlined style={{ color: '#1890ff' }} />
                                        <Text strong>Editar Configuração</Text>
                                    </Space>
                                }
                                extra={
                                    <Button type="text" onClick={() => setIsEditing(false)}>
                                        Fechar
                                    </Button>
                                }
                                style={{ 
                                    borderRadius: '16px', 
                                    boxShadow: '0 12px 32px rgba(0,0,0,0.08)',
                                    border: 'none'
                                }}
                            >
                                <Form
                                    form={form}
                                    layout="vertical"
                                    onFinish={handleSave}
                                >
                                    <div style={{ 
                                        background: '#fafafa', 
                                        padding: '20px', 
                                        borderRadius: '12px', 
                                        marginBottom: '24px',
                                        border: '1px solid #f0f0f0'
                                    }}>
                                        <Form.Item
                                            name="ngoma_availability"
                                            label={<Text strong style={{ fontSize: '15px' }}>Plataforma Ativa?</Text>}
                                            valuePropName="checked"
                                            style={{ margin: 0 }}
                                        >
                                            <Switch 
                                                checkedChildren="SISTEMA ONLINE" 
                                                unCheckedChildren="EM MANUTENÇÃO" 
                                                style={{ minWidth: '150px' }}
                                            />
                                        </Form.Item>
                                        <Text type="secondary" style={{ display: 'block', marginTop: '8px', fontSize: '13px' }}>
                                            Desligue este botão para colocar a plataforma inteira em modo de manutenção. Ninguém conseguirá aceder além dos administradores.
                                        </Text>
                                    </div>

                                    <Form.Item
                                        name="description"
                                        label={<Text strong>Mensagem aos Utilizadores</Text>}
                                        rules={[{ required: true, message: 'Insira a mensagem' }]}
                                    >
                                        <TextArea 
                                            rows={5} 
                                            placeholder="Descreva o motivo da manutenção..." 
                                            style={{ 
                                                borderRadius: '8px', 
                                                padding: '12px',
                                                fontSize: '15px' 
                                            }}
                                        />
                                    </Form.Item>
                                    <Text type="secondary" style={{ fontSize: '13px', display: 'block', marginTop: '-16px', marginBottom: '24px' }}>
                                        Esta mensagem será o único ecrã que os utilizadores verão quando o sistema estiver offline.
                                    </Text>

                                    <Form.Item style={{ margin: 0, textAlign: 'right' }}>
                                        <Space>
                                            <Button size="large" onClick={() => setIsEditing(false)} style={{ borderRadius: '8px' }}>
                                                Cancelar
                                            </Button>
                                            <Button 
                                                type="primary" 
                                                htmlType="submit" 
                                                icon={<SaveOutlined />} 
                                                loading={submitting}
                                                size="large"
                                                style={{ borderRadius: '8px', padding: '0 32px' }}
                                            >
                                                Guardar
                                            </Button>
                                        </Space>
                                    </Form.Item>
                                </Form>
                            </Card>
                        </Col>
                    )}
                </Row>
            )}
            <style>{`
                @keyframes fadeInRight {
                    from { opacity: 0; transform: translateX(30px); }
                    to { opacity: 1; transform: translateX(0); }
                }
            `}</style>
        </div>
    );
};

export default AdminDisponibilidade;
